import axios from "axios";
import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Input,
    Badge,
    Spinner,
    Button,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ListGroup,
    ListGroupItem,
    Toast,
    ToastBody,
    ToastHeader,
    Nav,
    NavItem,
    NavLink
} from "reactstrap";
import { useAuth } from "../../Provider/AuthProvider";
import { SearchResult } from "./Interfaces";
import { useLocation, useNavigate } from "react-router-dom";

interface Stock {
    entrepot_id: string;
    entrepot_name: string;
    stock: string;
}

interface SupplierOrderLine {
    id: string;
    product_id: string;
    ref: string;

    product_ref: string;
    desc: string;
    description: string;
    libelle: string;
    product_label: string;
    qty: string;

    subprice: string;
    total_ht: string;
    total_ttc: string;
    total_tva: string;
    tva_tx: string;
    pu_ht: string;
    rang: string;
    array_options: {
        options_origin: string;
        options_commandeorigine: string | null;
    };
}

interface ExpeditionLine {
    id: string;
    product_id: string;
    desc: string;
    qty: string;
    qty_shipped: string;
    subprice: string;
    ref: string;
    tva_tx: string;
    comment: string;
    stock: Stock[];
    commandefourndet?: string;
    fournisseurs?: SupplierOrderResponse | { [key: string]: { idwh: string; stock: number; warehouse_name: string, price: number } };
    supplierOrderData?: SupplierOrderData & {
        commandeFournId?: string;
        entity?: number;
        entityname?: string;
        message?: string;
        success?: boolean;
    };
    alreadyshipped?: number;
}

interface SupplierOrderData {
    id: string;
    ref: string;
    statut: string;
    date: string;
    date_validation: string | null;
    total_ht: string;
    total_ttc: string;
    total_tva: string;
    fourn_id: string;
    socid: string;
    entity: string;
    entityname: string;
    user_author_id: string;
    lines: SupplierOrderLine[];
    ref_ext?: string;
    ref_supplier?: string;
    mode_reglement?: string;
    cond_reglement?: string;
    note?: string;
    note_public?: string;
    note_private?: string;
    model_pdf?: string;
    brouillon?: number;
    billed?: string;
    remise_percent?: string;
    multicurrency_code?: string;
    multicurrency_total_ht?: string;
    multicurrency_total_ttc?: string;
    multicurrency_total_tva?: string;
    picto?: string;
    status?: string;
    fields?: any;
    linked_objects?: any[];
    comments?: any[];
    commandeFournId?: string;
    message?: string;
    success?: boolean;
    thirdparty?: {
        name: string
    }
}

interface SupplierOrderResponse {
    success: boolean;
    message: string;
    data?: SupplierOrderData;
    commandeFournId?: string;
    entity?: number;
    entityname?: string;
}

interface ExpeditionData {
    id: string;
    ref: string;
    status: string;
    orderinfo: {
        orderid: number;
        ref: string;
        client_name: string;
        created_by: string;
        date_creation: string;
    };
    lines: ExpeditionLine[];
}

interface SupplierInfo {
    idwh: string;
    stock: number;
    price: number;
    warehouse_name: string;
    supplier_name?: string;
}

interface ToastMessage {
    id: number;
    title: string;
    message: string;
    color: 'success' | 'danger' | 'warning' | 'info';
}   

const WaitingOrders = () => {
    const { user, order, changeOrder } = useAuth();
    const [comptoirOrders, setComptoirOrders] = useState<SearchResult[]>([]);
    const [franchisesOrders, setFranchisesOrders] = useState<SearchResult[]>([]);
    const [preparedQuantities, setPreparedQuantities] = useState<Record<string, number>>({});
    const [selectedWarehouses, setSelectedWarehouses] = useState<Record<string, string>>({});
    const [expeditionData, setExpeditionData] = useState<ExpeditionData | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [expeditionId, setExpeditionId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState<Record<string, boolean>>({});
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [createdOrderRef, setCreatedOrderRef] = useState("");

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalMessage, setModalMessage] = useState("");
    const [modalType, setModalType] = useState<"commander" | "voir">("commander");
    const [selectedLine, setSelectedLine] = useState<ExpeditionLine | null>(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [fournisseurId, setFournisseurId] = useState<string>("");
    const [availableSuppliers, setAvailableSuppliers] = useState<SupplierInfo[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState<string>("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmClosureModalOpen, setConfirmClosureModalOpen] = useState(false); // Nouveau modal pour clôture
    const location = useLocation();
    const navigate = useNavigate();
    // Toast notifications state
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    // Tab state
    const [activeTab, setActiveTab] = useState<'comptoir' | 'franchises'>('comptoir');
    const [allOrders, setAllOrders] = useState<SearchResult[]>([]);

    // Add toast notification
    const addToast = (title: string, message: string, color: 'success' | 'danger' | 'warning' | 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, title, message, color }]);

        // Auto remove toast after 5 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // Remove toast
    const removeToast = (id: number) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };

    const toggleDropdown = (lineId: string) => {
        setDropdownOpen(prev => ({
            ...prev,
            [lineId]: !prev[lineId]
        }));
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (!modalOpen) {
            setFournisseurId("");
            setAvailableSuppliers([]);
            setSelectedSupplier("");
        }
    };

    const openCommanderModal = (line: ExpeditionLine) => {
        setSelectedLine(line);
        setModalType("commander");
        setModalTitle("Commander le produit");
        setModalOpen(true);
        setModalLoading(false);

        if (line.fournisseurs && Object.keys(line.fournisseurs).length > 0) {
            const suppliers: SupplierInfo[] = Object.entries(line.fournisseurs).map(([supplierName, data]) => ({
                idwh: data.idwh,
                stock: data.stock,
                comment: data.comment,
                price: data.price,
                warehouse_name: data.warehouse_name,
                supplier_name: supplierName
            }));
            setAvailableSuppliers(suppliers);
            if (suppliers.length > 0) {
                setSelectedSupplier(suppliers[0].idwh);
                setModalMessage(`Sélectionnez un fournisseur pour: ${line.ref} - ${line.desc}`);
            } else {
                setModalMessage(`Aucun fournisseur disponible pour ce produit.`);
            }
        } else {
            setModalMessage(`Aucun fournisseur disponible pour ce produit.`);
            setAvailableSuppliers([]);
        }
    };

    const openVoirModal = async (line: ExpeditionLine) => {
        setSelectedLine(line);
        setModalType("voir");
        setModalTitle("Détails de la commande fournisseur");
        setModalOpen(true);
        setModalLoading(true);

        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                {
                    action: 'getcommandefourn',
                    commandefourndet: parseInt(line.commandefourndet || '0'),
                    entity: user.entity
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data) {
                console.log("Supplier Order Details:", response.data);
                console.log("line.commandefourndet :: " + line.commandefourndet);
                setSelectedLine(prev => prev ? {
                    ...prev,
                    supplierOrderData: response.data
                } : prev);
                setModalMessage(`Commande fournisseur: ${response.data.ref}`);
            } else {
                setModalMessage(`Aucune commande fournisseur trouvée pour ce produit.`);
            }
        } catch (error) {
            console.error("Error fetching supplier order details:", error);
            setModalMessage("Erreur lors de la récupération des détails du fournisseur.");
        } finally {
            setModalLoading(false);
        }
    };

    useEffect(() => {
        const handleAutoSelectFromLivrer = async () => {
            // Check if we're coming from OrdersToDispatchPage with an order to auto-select
            const locationState = location.state as any;
            if (locationState?.fromLivrer && locationState?.autoSelectOrderId) {
                // Find the order in the lists
                const allOrders = [...comptoirOrders, ...franchisesOrders];
                const foundOrder = allOrders.find(o =>
                    o.id === locationState.autoSelectOrderId.toString() ||
                    o.ref === locationState.autoSelectOrderRef
                );

                if (foundOrder) {
                    // Select the order
                    changeOrder(foundOrder);
                    await fetchExpeditionDetails(parseInt(foundOrder.id));

                    // Show success message
                    addToast("Succès", "Commande prête pour expédition!", "success");

                    // Clear the state to prevent re-triggering
                    navigate(location.pathname, { replace: true, state: {} });
                } else {
                    // If order not found immediately, try again after fetching
                    const timeoutId = setTimeout(() => {
                        const updatedOrders = [...comptoirOrders, ...franchisesOrders];
                        const foundAgain = updatedOrders.find(o =>
                            o.id === locationState.autoSelectOrderId.toString() ||
                            o.ref === locationState.autoSelectOrderRef
                        );

                        if (foundAgain) {
                            changeOrder(foundAgain);
                            fetchExpeditionDetails(parseInt(foundAgain.id));
                            addToast("Succès", "Commande prête pour expédition!", "success");
                            navigate(location.pathname, { replace: true, state: {} });
                        }
                    }, 1000);

                    return () => clearTimeout(timeoutId);
                }
            }
        };

        handleAutoSelectFromLivrer();
    }, [location.state, comptoirOrders, franchisesOrders, navigate]);


    const handleCreateSupplierOrder = async () => {
        if (!selectedLine || !selectedSupplier) {
            addToast("Erreur", "Veuillez sélectionner un fournisseur", "warning");
            return;
        }

        setModalLoading(true);
        const data = {
            action: 'setcommandefourn',
            exp_line_id: parseInt(selectedLine.id),
            fournisseur: selectedSupplier,
            entity: user.entity
        };

        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data) {
                setCreatedOrderRef(response.data.ref);
                setSuccessModalOpen(true);
                addToast("Succès", "Commande fournisseur créée avec succès", "success");

                if (order) {
                    await fetchExpeditionDetails(parseInt(order.id));
                }
                toggleModal();
            } else {
                addToast("Erreur", "Erreur lors de la création de la commande fournisseur", "danger");
            }
        } catch (error) {
            console.error('Error creating supplier order:', error);
            addToast("Erreur", "Erreur lors de la création de la commande fournisseur", "danger");
        } finally {
            setModalLoading(false);
        }
    };

    const fetchCommandes = async () => {
        try {
            const response = await axios.post(
                "/apiV2/",
                { action: "waitingorders", entity: user.entity },
                {
                    baseURL: "https://api.apapiecesauto.fr",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${user.token}`,
                    },
                }
            );

            const comptoirList: SearchResult[] = response.data.comptoir || [];
            const franchisesList: SearchResult[] = response.data.franchises || [];

            console.log("Fetched Comptoir Orders:", comptoirList);
            console.log("Fetched Franchises Orders:", franchisesList);

            setComptoirOrders(comptoirList);
            setFranchisesOrders(franchisesList);

            // Set all orders based on active tab
            const currentOrders = activeTab === 'comptoir' ? comptoirList : franchisesList;
            setAllOrders(currentOrders);

        } catch (error) {
            console.error("Error fetching orders:", error);
            addToast("Erreur", "Erreur lors du chargement des commandes", "danger");
        }
    };

    const fetchExpeditionDetails = async (orderId: number) => {
        setDetailsLoading(true);
        const data = {
            action: 'getexpedition',
            orderid: orderId,
            entity: user.entity
        }
        try {
            const response = await axios.post(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.data && response.data.length > 0) {
                const expedition = response.data[0];
                const mappedExpedition: ExpeditionData = {
                    id: expedition.id,
                    ref: expedition.ref,
                    orderinfo: expedition.orderinfo,
                    status: expedition.status,
                    lines: expedition.lines.map((line: any) => ({
                        id: line.id,
                        product_id: line.product_id,
                        ref: line.ref || line.product_ref || '',
                        desc: line.desc,
                        libelle: line.libelle,
                        qty: line.qty,
                        alreadyshipped: line.alreadyshipped || 0,

                        qty_shipped: line.qty_shipped,
                        subprice: line.subprice,
                        tva_tx: line.tva_tx,
                        stock: line.stock || [],
                        comment: line.comment || "",
                        commandefourndet: line.commandefourndet,

                        fournisseurs: line.fournisseurs
                    }))
                };

                setExpeditionData(mappedExpedition);
                setExpeditionId(expedition.ref);

                const initialQuantities: Record<string, number> = {};
                const initialWarehouses: Record<string, string> = {};

                if (mappedExpedition.lines) {
                    mappedExpedition.lines.forEach((line: ExpeditionLine) => {
                        // Initialiser à 0 - l'utilisateur saisira ce qu'il veut préparer maintenant
                        initialQuantities[line.id] = 0;

                        if (line.stock && line.stock.length > 0) {
                            initialWarehouses[line.id] = line.stock[0].entrepot_id;
                        }
                    });
                }

                setPreparedQuantities(initialQuantities);
                setSelectedWarehouses(initialWarehouses);
            }
        } catch (error) {
            console.error('Error fetching expedition details:', error);
            addToast("Erreur", "Erreur lors du chargement des détails de l'expédition", "danger");
        } finally {
            setDetailsLoading(false);
        }
    };

    
    
// Fonction pour clôturer la commande
const saveExpedition = async () => {
    if (!expeditionData) return;
    setSaving(true);

    try {
        // Vérifier si on est en mode clôture
        const isClosingOrder = confirmClosureModalOpen && preparedQuantities && 
                              Object.values(preparedQuantities).every(qty => qty === 0);
        
        // Construire l'objet data de base
        const data: any = {
            action: 'setexpedition',
            expeditionid: parseInt(expeditionData.id),
            entity: user.entity
        };

        if (isClosingOrder) {
            //  pour commande de franchise
            data.lines = []; 
            data.closed = 1; 
        } else {
            // Mode normal d'expédition
            const lines = Object.entries(preparedQuantities)
                .filter(([_, quantity]) => quantity > 0) 
                .map(([lineId, newQuantity]) => ({
                    id: parseInt(lineId),
                    qty_shipped: newQuantity
                }));
            
            data.lines = lines;
            
        }
        
        const response = await axios.post(
            'https://api.apapiecesauto.fr/apiV2/',
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                }
            }
        );

        if (response.data) {
            if (isClosingOrder) {
                // Cas de clôture
                changeOrder(null);
                setExpeditionData(null);
                setExpeditionId(null);
                setPreparedQuantities({});
                setSelectedWarehouses({});
                
                addToast("Succès", "Commande clôturée avec succès!", "success");
                await fetchCommandes();
                setConfirmClosureModalOpen(false);
            } else {
                // Cas d'expédition normale
                const isComplete = expeditionData.lines.every(line => {
                    const alreadyShipped = line.alreadyshipped || 0;
                    const preparedNow = preparedQuantities[line.id] || 0;
                    const orderedQty = parseInt(line.qty);
                    return (alreadyShipped + preparedNow) >= orderedQty;
                });

                if (isComplete) {
                    changeOrder(null);
                    setExpeditionData(null);
                    setExpeditionId(null);
                    setPreparedQuantities({});
                    setSelectedWarehouses({});

                    addToast("Succès", "Expédition complétée avec succès! La commande a été retirée de la liste.", "success");
                    await fetchCommandes();
                } else {
                    if (order) {
                        await fetchExpeditionDetails(parseInt(order.id));
                    }
                    addToast("Succès", "Expédition partiellement validée avec succès!", "success");
                }
            }
        } else {
            addToast("Erreur", "Erreur lors de l'opération", "danger");
        }
    } catch (error) {
        console.error('Error:', error);
        addToast("Erreur", "Erreur lors de l'opération", "danger");
    } finally {
        setSaving(false);
    }
};





    const handleQuantityChange = (lineId: string, value: string) => {
        const line = expeditionData?.lines.find(line => line.id === lineId);

        if (!line) return;

        const orderedQty = parseInt(line.qty);
        const alreadyShipped = line.alreadyshipped || 0;
        const maxPreparable = Math.max(0, orderedQty - alreadyShipped);

        // Si la valeur est vide ou non numérique, mettre à 0
        if (value === '' || isNaN(parseInt(value))) {
            setPreparedQuantities(prev => ({
                ...prev,
                [lineId]: 0
            }));
            return;
        }

        let quantity = parseInt(value);

        // Empêcher les valeurs négatives
        if (quantity < 0) {
            addToast("Attention", `Quantité ne peut pas être négative`, "warning");
            quantity = 0;
        }

        // Empêcher de dépasser le maximum préparable
        if (quantity > maxPreparable) {
            addToast("Erreur", `Quantité ne peut pas dépasser ${maxPreparable} (différence entre commandé et déjà expédié)`, "danger");
            quantity = maxPreparable;
        }

        setPreparedQuantities(prev => ({
            ...prev,
            [lineId]: quantity
        }));
    };

    const areAllQuantitiesValid = () => {
        if (!expeditionData) return false;

        return expeditionData.lines.every(line => {
            const preparedQty = preparedQuantities[line.id] || 0;
            const orderedQty = parseInt(line.qty);
            const alreadyShipped = line.alreadyshipped || 0;
            const maxPreparable = Math.max(0, orderedQty - alreadyShipped);

            // Vérifier que la quantité est entre 0 et le maximum préparale
            return preparedQty >= 0 && preparedQty <= maxPreparable;
        });
    };

    const isAnyQuantityPrepared = () => {
        if (!expeditionData) return false;

        return expeditionData.lines.some(line => {
            const preparedQty = preparedQuantities[line.id] || 0;
            return preparedQty > 0;
        });
    };

    const handleValidateExpedition = () => {
        if (!expeditionData) {
            addToast("Attention", "Aucune expédition sélectionnée", "warning");
            return;
        }

        // Vérifier si au moins une quantité est préparée
        if (!isAnyQuantityPrepared()) {
            addToast("Erreur", "Aucune quantité à préparer. Veuillez saisir au moins une quantité.", "danger");
            return;
        }

        // Vérifier si toutes les quantités sont valides
        const invalidLines = expeditionData.lines.filter(line => {
            const preparedQty = preparedQuantities[line.id] || 0;
            const orderedQty = parseInt(line.qty);
            const alreadyShipped = line.alreadyshipped || 0;
            const maxPreparable = Math.max(0, orderedQty - alreadyShipped);

            return preparedQty < 0 || preparedQty > maxPreparable;
        });

        if (invalidLines.length > 0) {
            addToast("Erreur",
                `${invalidLines.length} ligne(s) ont des quantités invalides. ` +
                "La quantité ne peut pas être inférieure à 0 ni supérieure à la différence entre quantité commandée et déjà expédié.",
                "danger"
            );
            return;
        }

        setConfirmModalOpen(true);
    };

    const handleWarehouseChange = (lineId: string, warehouseId: string) => {
        setSelectedWarehouses(prev => ({
            ...prev,
            [lineId]: warehouseId
        }));
    };

    const totalPrepared = expeditionData?.lines.reduce((sum, line) => {
        const preparedNow = preparedQuantities[line.id] || 0;
        return sum + preparedNow;
    }, 0) || 0;

    const totalAlreadyShipped = expeditionData?.lines.reduce((sum, line) => {
        return sum + (line.alreadyshipped || 0);
    }, 0) || 0;

    const totalOrdered = expeditionData?.lines.reduce((sum, line) => sum + parseInt(line.qty), 0) || 0;

    const isExpeditionComplete = expeditionData?.lines.every(line => {
        const alreadyShipped = line.alreadyshipped || 0;
        const orderedQty = parseInt(line.qty);
        return alreadyShipped >= orderedQty;
    }) || false;

    const isAllLinesValidated = () => {
        if (!expeditionData) return false;

        return expeditionData.lines.some(line => {
            const selectedWarehouse = selectedWarehouses[line.id] ||
                (line.stock.length > 0 ? line.stock[0].entrepot_id : '');
            const selectedStock = line.stock.find(s => s.entrepot_id === selectedWarehouse);
            const preparedQty = preparedQuantities[line.id] || 0;
            const orderedQty = parseInt(line.qty);
            const stockQty = selectedStock ? parseInt(selectedStock.stock) : 0;
            return stockQty > 0 && preparedQty != 0;
        });
    };

    // Fonction pour vérifier si on peut clôturer la commande


const canCloseOrder = () => {
    if (!expeditionData || !order) return false;
    
    // Vérifier si c'est une commande de franchise
    const isFranchiseOrder = franchisesOrders.some(fOrder => 
        fOrder.id === order.id || fOrder.ref === order.ref
    );
    
    // Si ce n'est pas une commande de franchise, ne pas afficher le bouton
    if (!isFranchiseOrder) return false;
    
    // Condition 1: Aucune quantité préparée maintenant
    const noQuantityPreparedNow = !isAnyQuantityPrepared();
    
    // Condition 2: Quantité expédiée < quantité commandée
    const notFullyShipped = totalAlreadyShipped < totalOrdered;
    
    // Condition 3: Quantité expédiée = 0 ET préparée = 0
    const noShipmentAtAll = totalAlreadyShipped === 0 && totalPrepared === 0;
    
    // Condition 4: Quantité commandée = quantité expédiée ET quantité préparée = 0
    const fullyShippedNoPreparation = totalAlreadyShipped === totalOrdered && totalPrepared === 0;
    
    // Condition 5: Commande partiellement expédiée et aucune préparation en cours
    const partiallyShippedNoPreparation = totalAlreadyShipped > 0 && 
                                         totalAlreadyShipped < totalOrdered && 
                                         totalPrepared === 0;
    
    return (noQuantityPreparedNow && notFullyShipped) || 
           noShipmentAtAll || 
           fullyShippedNoPreparation ||
           partiallyShippedNoPreparation;
};

    useEffect(() => {
        fetchCommandes();
        const intervalId = setInterval(fetchCommandes, 30000);
        return () => clearInterval(intervalId);
    }, []);

    // Update allOrders when activeTab changes
    useEffect(() => {
        const currentOrders = activeTab === 'comptoir' ? comptoirOrders : franchisesOrders;
        setAllOrders(currentOrders);
    }, [activeTab, comptoirOrders, franchisesOrders]);

    const handleCommandeSelect = async (selectedOrder: SearchResult) => {
        changeOrder(selectedOrder);
        const orderId = parseInt(selectedOrder.id);
        await fetchExpeditionDetails(orderId);
    };

    return (
        <React.Fragment>
            <style>
                {`
    @keyframes blink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
    }
    body {
        font-family: 'Inter', sans-serif;
        background-color: #F0F8FF;
    }
    .modern-card {
        background-color: #FFFFFF;
        border-radius: 10px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #E1E8F0;
    }
    .modern-card-header {
        background: linear-gradient(135deg, #0077B6 0%, #0096C7 100%);
        color: #FFFFFF;
        border-radius: 10px 10px 0 0;
        padding: 14px 20px;
        border-bottom: none;
    }
    .modern-table {
        border-collapse: separate;
        border-spacing: 0;
        font-size: 12px !important;
        width: 100%;
    }
    .modern-table th {
        background: linear-gradient(to bottom, #F8FCFD 0%, #E6F7FF 100%);
        color: #2D3748;
        font-weight: 600;
        padding: 10px 12px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
        border-bottom: 2px solid #CAF0F8;
    }
    .modern-table td {
        padding: 10px 12px;
        font-size: 12px;
        color: #2D3748;
        border-bottom: 1px solid #F0F9FF;
        vertical-align: middle;
    }
    .modern-table tr:nth-child(odd) {
        background-color: #F8FCFD;
    }
    .modern-table tr:hover {
        background-color: #E6F7FF;
    }
    .commande-list-item {
        transition: all 0.2s ease;
        border-radius: 8px;
    }
    .commande-list-item:hover {
        background-color: #F0F9FF;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 119, 182, 0.08);
    }
    .commande-list-item.active {
        background: linear-gradient(135deg, #E6F7FF 0%, #CAF0F8 100%);
        border: 2px solid #0077B6;
        box-shadow: 0 0 0 2px rgba(0, 119, 182, 0.1);
    }
    .modern-input {
        border: 1px solid #90E0EF;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: 12px;
        transition: all 0.2s ease;
        height: 36px;
        background-color: #FFFFFF;
    }
    .modern-input:focus {
        border-color: #0077B6;
        box-shadow: 0 0 0 3px rgba(0, 119, 182, 0.15);
        outline: none;
    }
    .modern-input:disabled {
        background-color: #F8FCFD;
        color: #94A3B8;
        cursor: not-allowed;
        border-color: #E1E8F0;
    }
    .modern-badge {
        background: linear-gradient(135deg, #00B4D8 0%, #0096C7 100%);
        color: #FFFFFF;
        font-size: 10px;
        padding: 3px 8px;
        border-radius: 12px;
        font-weight: 500;
    }
    .no-commande-card {
        background-color: #FFFFFF;
        border-radius: 10px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #E1E8F0;
        text-align: center;
        padding: 32px;
    }
    .commande-list-container {
        background-color: #FFFFFF;
        border-radius: 10px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
        border: 1px solid #E1E8F0;
        width: 100%;
    }
    .validate-btn {
        background: linear-gradient(135deg, #00A896 0%, #02C39A 100%);
        border: none;
        color: white;
        font-weight: 600;
        padding: 10px 24px;
        border-radius: 8px;
        transition: all 0.2s ease;
        font-size: 13px;
    }
    .validate-btn:hover {
        background: linear-gradient(135deg, #028090 0%, #00A896 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 168, 150, 0.3);
    }
    .validate-btn:disabled {
        background: linear-gradient(135deg, #B0C4DE 0%, #A0C8E0 100%);
        cursor: not-allowed;
    }
    .close-order-btn {
        background: linear-gradient(135deg, #E63946 0%, #FF6B6B 100%);
        border: none;
        color: white;
        font-weight: 600;
        padding: 10px 24px;
        border-radius: 8px;
        transition: all 0.2s ease;
        font-size: 13px;
    }
    .close-order-btn:hover {
        background: linear-gradient(135deg, #D00000 0%, #E63946 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(230, 57, 70, 0.3);
    }
    .close-order-btn:disabled {
        background: linear-gradient(135deg, #B0C4DE 0%, #A0C8E0 100%);
        cursor: not-allowed;
    }
    .dropdown-toggle-custom {
        background: white !important;
        color: #2D3748 !important;
        border: 1px solid #90E0EF !important;
        min-width: 120px;
        font-size: 12px;
        padding: 6px 12px;
        height: 36px;
        border-radius: 8px;
    }
    .dropdown-toggle-custom:focus {
        box-shadow: 0 0 0 3px rgba(0, 119, 182, 0.15) !important;
        border-color: #0077B6 !important;
    }
    .commande-title {
        font-size: 13px;
        color: white;
        margin: 0;
        font-weight: 600;
    }
    .action-btn {
        padding: 4px 10px;
        font-size: 11px;
        border-radius: 6px;
        min-width: 80px;
        height: 32px;
        font-weight: 500;
        border: none;
    }
    .commander-btn {
        background: linear-gradient(135deg, #0077B6 0%, #0096C7 100%);
        color: white;
    }
    .commander-btn:hover {
        background: linear-gradient(135deg, #005885 0%, #0077B6 100%);
        color: white;
    }
    .commander-btn:disabled {
        background: linear-gradient(135deg, #B0C4DE 0%, #A0C8E0 100%);
        cursor: not-allowed;
    }
    .voir-btn {
        background: linear-gradient(135deg, #00A896 0%, #02C39A 100%);
        color: white;
    }
    .voir-btn:hover {
        background: linear-gradient(135deg, #028090 0%, #00A896 100%);
        color: white;
    }
    .supplier-list-item {
        cursor: pointer;
        transition: all 0.2s ease;
        padding: 8px 12px !important;
        font-size: 12px;
        border-radius: 6px;
    }
    .supplier-list-item:hover {
        background-color: #F0F9FF;
        transform: translateY(-1px);
    }
    .supplier-list-item.selected {
        background: linear-gradient(135deg, #E6F7FF 0%, #CAF0F8 100%);
        border: 2px solid #0077B6;
    }
    .success-modal .modal-header {
        background: linear-gradient(135deg, #00A896 0%, #02C39A 100%) !important;
        color: white !important;
        border-bottom: none;
    }
    .success-modal .modal-content {
        border: 2px solid #00A896;
        border-radius: 12px;
    }
    .success-modal .btn-success {
        background: linear-gradient(135deg, #00A896 0%, #02C39A 100%);
        border: none;
    }
    .success-modal .btn-success:hover {
        background: linear-gradient(135deg, #028090 0%, #00A896 100%);
    }
    .toast-container {
        position: fixed;
        top: 80px;
        right: 20px;
        z-index: 9999;
        min-width: 300px;
    }
    .toast-success {
        border-left: 4px solid #00A896;
        background: #F0FDFA;
    }
    .toast-danger {
        border-left: 4px solid #E63946;
        background: #FFF0F2;
    }
    .toast-warning {
        border-left: 4px solid #F4A261;
        background: #FFF8F0;
    }
    .toast-info {
        border-left: 4px solid #0077B6;
        background: #F0F9FF;
    }
    .quantity-info {
        font-size: 10px;
        color: #5A6C7D;
        display: block;
        margin-top: 2px;
    }
    .dropdown-menu-custom {
        max-height: 200px;
        overflow-y: auto;
        font-size: 12px;
        min-width: 180px;
        border: 1px solid #E1E8F0;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 119, 182, 0.1);
    }
    .dropdown-item-custom {
        padding: 6px 12px !important;
        font-size: 12px;
        border-radius: 4px;
        margin: 2px;
    }
    .dropdown-item-custom:hover {
        background-color: #F0F9FF;
    }
    .dropdown-item-custom.active {
        background: linear-gradient(135deg, #E6F7FF 0%, #CAF0F8 100%);
        color: #0077B6;
    }
    .table-responsive {
        max-width: 100%;
        overflow-x: auto;
    }
    .dropdown-toggle-sm {
        padding: 4px 10px !important;
        font-size: 11px !important;
        height: 30px !important;
    }
    .tab-header {
        display: flex;
        justify-content: center;
        margin-bottom: 12px;
        background: #F0F8FF;
        border-radius: 8px;
        padding: 4px;
        border: 1px solid #CAF0F8;
    }
    .tab-nav-link {
        padding: 8px 20px !important;
        font-size: 13px !important;
        font-weight: 600;
        color: #5A6C7D;
        border-radius: 6px;
        transition: all 0.2s ease;
        border: none !important;
        background: transparent;
    }
    .tab-nav-link.active {
        background: linear-gradient(135deg, #0077B6 0%, #0096C7 100%) !important;
        color: white !important;
        box-shadow: 0 2px 4px rgba(0, 119, 182, 0.2);
    }
    .tab-nav-link:hover {
        background-color: #E6F7FF;
        color: #2D3748;
    }
    .orders-scroll-container {
        max-height: calc(100vh - 300px);
        overflow-y: auto;
        padding-right: 4px;
    }
    .orders-scroll-container::-webkit-scrollbar {
        width: 6px;
    }
    .orders-scroll-container::-webkit-scrollbar-track {
        background: #E6F7FF;
        border-radius: 10px;
    }
    .orders-scroll-container::-webkit-scrollbar-thumb {
        background: #90E0EF;
        border-radius: 10px;
    }
    .orders-scroll-container::-webkit-scrollbar-thumb:hover {
        background: #00B4D8;
    }
    .text-truncate-custom {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
    }
    .product-ref {
        color: #0077B6 !important;
    }
`}
            </style>
            {/* Toast Notifications Container */}
            <div className="toast-container">
                {toasts.map(toast => (
                    <Toast key={toast.id} className={`mb-2 ${toast.color === 'success' ? 'toast-success' : ''} ${toast.color === 'danger' ? 'toast-danger' : ''} ${toast.color === 'warning' ? 'toast-warning' : ''} ${toast.color === 'info' ? 'toast-info' : ''}`}>
                        <ToastHeader toggle={() => removeToast(toast.id)} className={`bg-${toast.color} text-white`}>
                            {toast.title}
                        </ToastHeader>
                        <ToastBody>
                            {toast.message}
                        </ToastBody>
                    </Toast>
                ))}
            </div>

            

           {/* Modal de confirmation pour clôture de commande */}
<Modal isOpen={confirmClosureModalOpen} toggle={() => setConfirmClosureModalOpen(false)} centered>
    <ModalHeader toggle={() => setConfirmClosureModalOpen(false)} className="fs-16">
        <i className="ri-alert-line me-2 text-danger"></i>
        Confirmation de clôture
    </ModalHeader>
    <ModalBody className="py-4">
        <div className="text-center mb-3">
            <i className="ri-error-warning-line display-4 text-danger"></i>
            <h5 className="mt-3">Êtes-vous sûr de vouloir clôturer cette commande ?</h5>
        </div>
        <div className="alert alert-danger">
            <i className="ri-information-line me-2"></i>
            <strong>Attention :</strong> Cette action est irréversible. La commande sera marquée comme terminée sans expédition supplémentaire.
        </div>
        <div className="alert alert-info">
            <i className="ri-information-line me-2"></i>
            <div>
                <strong>Résumé :</strong>
                <ul className="mb-0 mt-2">
                    <li>Quantité commandée : {totalOrdered}</li>
                    <li>Déjà expédiée : {totalAlreadyShipped}</li>
                    <li>À préparer maintenant : {totalPrepared}</li>
                </ul>
            </div>
        </div>
    </ModalBody>
    <ModalFooter className="text-center" style={{ textAlign: 'center' }}>
        <Button
            color="secondary"
            onClick={() => setConfirmClosureModalOpen(false)}
            className="fs-14"
        >
            <i className="ri-close-line me-1"></i>
            Annuler
        </Button>
        <Button
            color="danger"
            onClick={saveExpedition} 
            className="fs-14"
            disabled={saving}
        >
            {saving ? (
                <>
                    <Spinner size="sm" className="me-2" />
                    Clôture en cours...
                </>
            ) : (
                <>
                    <i className="ri-close-circle-line me-1"></i>
                    Clôturer la commande
                </>
            )}
        </Button>
    </ModalFooter>
</Modal>

            {/* Confirmation Modal */}
            <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)} centered>
                <ModalHeader toggle={() => setConfirmModalOpen(false)} className="fs-16">
                    <i className="ri-alert-line me-2 text-warning"></i>
                    Confirmation
                </ModalHeader>
                <ModalBody className="py-4">
                    <div className="text-center mb-3">
                        <i className="ri-question-line display-4 text-warning"></i>
                        <h5 className="mt-3">Êtes-vous sûr de vouloir valider cette expédition ?</h5>
                    </div>
                    <div className="alert alert-info">
                        <i className="ri-information-line me-2"></i>
                        Vérifiez que toutes les quantités préparées sont correctes.
                    </div>
                </ModalBody>
                <ModalFooter className="text-center" style={{ textAlign: 'center' }}>
                    <Button
                        color="secondary"
                        onClick={() => setConfirmModalOpen(false)}
                        className="fs-14"
                    >
                        <i className="ri-close-line me-1"></i>
                        Annuler
                    </Button>
                    <Button
                        color="primary"
                        onClick={async () => {
                            setConfirmModalOpen(false);
                            await saveExpedition();
                        }}
                        className="fs-14"
                    >
                        <i className="ri-check-line me-1"></i>
                        Valider l'expédition
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Success Modal */}
            <Modal isOpen={successModalOpen} toggle={() => setSuccessModalOpen(false)} centered>
                <ModalHeader toggle={() => setSuccessModalOpen(false)} className="">
                    <i className="ri-checkbox-circle-line me-2"></i>
                    Commande créée avec succès
                </ModalHeader>
                <ModalBody className="text-center py-4">
                    <div className="mb-3">
                        <i className="ri-checkbox-circle-fill text-success display-3"></i>
                    </div>
                    <h5 className="text-success mb-3">Commande fournisseur créée !</h5>
                    <p className="mb-0">
                        Référence de la commande : <strong>{createdOrderRef}</strong>
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="success"
                        onClick={() => {
                            setSuccessModalOpen(false);
                            if (order) {
                                fetchExpeditionDetails(parseInt(order.id));
                            }
                        }}
                    >
                        <i className="ri-check-line me-1"></i> OK
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Commander/Voir Modal */}
            <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>{modalTitle}</ModalHeader>
                <ModalBody>
                    {modalLoading ? (
                        <div className="text-center py-4">
                            <Spinner color="primary" />
                            <div className="mt-2">Chargement en cours...</div>
                        </div>
                    ) : (
                        <>
                            <p className="mb-3">{modalMessage}</p>
                            {selectedLine && modalType === "commander" && (
                                <div className="mt-3">
                                    {availableSuppliers.length > 0 ? (
                                        <>
                                            <h6 className="mb-3">Sélectionnez un fournisseur:</h6>
                                            <ListGroup>
                                                {availableSuppliers.map((supplier, index) => (
                                                    <ListGroupItem
                                                        key={index}
                                                        tag="button"
                                                        action
                                                        className={`supplier-list-item ${selectedSupplier === supplier.idwh ? 'selected' : ''}`}
                                                        onClick={() => setSelectedSupplier(supplier.idwh)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div>
                                                                <strong>{supplier.idwh}</strong>
                                                                <div className="text-muted small">
                                                                    {supplier.supplier_name || supplier.warehouse_name}
                                                                </div>
                                                                <strong>{supplier.price.toFixed(2)} €</strong>
                                                            </div>
                                                            {selectedSupplier === supplier.idwh && (
                                                                <i className="ri-check-line text-primary"></i>
                                                            )}
                                                        </div>
                                                    </ListGroupItem>
                                                ))}
                                            </ListGroup>
                                        </>
                                    ) : (
                                        <div className="text-center py-4">
                                            <i className="ri-error-warning-line display-5 text-muted"></i>
                                            <p className="text-muted mt-2">Aucun fournisseur disponible pour ce produit.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            {selectedLine && modalType === "voir" && selectedLine.supplierOrderData && (
                                <div className="mt-4">
                                    <Card className="modern-card">
                                        <CardBody>
                                            {selectedLine.supplierOrderData ? (
                                                <>
                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Référence:</small>
                                                                <div className="fw-bold">{selectedLine.supplierOrderData.ref || "N/A"}</div>
                                                            </div>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Statut:</small>
                                                                    <Badge
                                                                        color={
                                                                            selectedLine.supplierOrderData.statut === "0" ? "warning" :
                                                                                selectedLine.supplierOrderData.statut === "1" || selectedLine.supplierOrderData.statut === "2" ? "info" :
                                                                                    selectedLine.supplierOrderData.statut === "4" ? "primary" :
                                                                                        selectedLine.supplierOrderData.statut === "5" ? "success" :
                                                                                            selectedLine.supplierOrderData.statut === "6" ? "danger" : "secondary"
                                                                        }
                                                                        className="ms-2"
                                                                    >
                                                                        {
                                                                            selectedLine.supplierOrderData.statut === "0" ? "Brouillon" :
                                                                                selectedLine.supplierOrderData.statut === "1" || selectedLine.supplierOrderData.statut === "2" ? "Commandé" :
                                                                                    selectedLine.supplierOrderData.statut === "4" ? "Partiellement reçu" :
                                                                                        selectedLine.supplierOrderData.statut === "5" ? "Reçu" :
                                                                                            selectedLine.supplierOrderData.statut === "6" || selectedLine.supplierOrderData.statut === "7" ? "Annulé" :
                                                                                                "Inconnu"
                                                                        }
                                                                    </Badge>
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Date:</small>
                                                                <div className="fw-bold">
                                                                    {selectedLine.supplierOrderData.date && !isNaN(Number(selectedLine.supplierOrderData.date)) ?
                                                                        new Date(Number(selectedLine.supplierOrderData.date) * 1000).toLocaleDateString() :
                                                                        "N/A"}
                                                                </div>
                                                                <div className="">
                                                                    {selectedLine.supplierOrderData?.thirdparty && (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <small className="text-muted">Nom du fournisseur:</small>
                                                                                <div className="fw-bold">{selectedLine.supplierOrderData.thirdparty.name || "N/A"}</div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <div className="table-responsive mt-4">
                                                        <table className="table table-sm modern-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Référence</th>
                                                                    <th>Description</th>
                                                                    <th className="text-center">Quantité</th>
                                                                    <th className="text-left">Réception</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {selectedLine.supplierOrderData.lines &&
                                                                    selectedLine.supplierOrderData.lines.length > 0 ? (
                                                                    selectedLine.supplierOrderData.lines.map((lineItem: SupplierOrderLine) => (
                                                                        <tr key={lineItem.id}>
                                                                            <td className="fw-semibold">{lineItem.product_ref}</td>
                                                                            <td>{lineItem.product_label || lineItem.libelle}</td>
                                                                            <td className="text-center">{lineItem.qty}</td>
                                                                            <td className="text-left">---</td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={5} className="text-center">
                                                                            Aucune ligne de commande
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-4">
                                                    <i className="ri-error-warning-line display-5 text-muted"></i>
                                                    <p className="text-muted mt-2">Aucune donnée de commande disponible.</p>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card>
                                </div>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>
                        {modalType === "commander" ? "Annuler" : "Fermer"}
                    </Button>
                    {modalType === "commander" && (
                        <Button
                            color="primary"
                            onClick={handleCreateSupplierOrder}
                            disabled={!selectedSupplier || modalLoading}
                        >
                            {modalLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Création...
                                </>
                            ) : (
                                "Créer la commande"
                            )}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            <div className="page-content pb-0">
                <Container fluid>
                    <Row>
                        <Col xl="12" lg="12" style={{ paddingRight: '8px' }}>
                            <Row>
                                <Col xl="9" lg="8" style={{ paddingRight: '4px' }}>
                                    {order ? (
                                        <Card className="mb-4 modern-card">
                                            <CardHeader className="modern-card-header" style={{
                                                backgroundColor: "#0077B6",
                                                padding: "8px 12px"
                                            }}>
                                                <Row className="align-items-center">
                                                    <Col md={3}>
                                                        {expeditionId && (
                                                            <Badge color="success" style={{ fontSize: '10px' }}>
                                                                Expedition: {expeditionId}
                                                            </Badge>
                                                        )}
                                                    </Col>
                                                    <Col md={6} className="text-center">
                                                        {expeditionData && order.ref && (
                                                            <h5 className="commande-title mb-0">
                                                                Commande : {order.ref}
                                                            </h5>
                                                        )}
                                                    </Col>
                                                    <Col md={3} className="text-end">
                                                        <span style={{ fontSize: '10px', color: '#F3F4F6' }}>
                                                            Date: {new Date(order.date).toLocaleDateString()}
                                                        </span>
                                                    </Col>
                                                </Row>
                                                {expeditionData && (
                                                    <Row className="mt-2">
                                                        <Col md={6}>
                                                            <small style={{ color: '#F3F4F6', fontSize: '10px' }}>
                                                                Client: {expeditionData.orderinfo.client_name}
                                                            </small>
                                                        </Col>
                                                        <Col md={6} className="text-end">
                                                            <small style={{ color: '#F3F4F6', fontSize: '10px' }}>
                                                                Créé par: {expeditionData.orderinfo.created_by}
                                                            </small>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </CardHeader>
                                            <CardBody style={{ padding: '12px' }}>
                                                {detailsLoading ? (
                                                    <div className="text-center py-4">
                                                        <Spinner color="primary" />
                                                        <div className="mt-2" style={{ fontSize: '12px' }}>Chargement des détails...</div>
                                                    </div>
                                                ) : expeditionData ? (
                                                    <>
                                                        <div className="table-responsive" style={{ maxWidth: '100%' }}>
                                                            <table className="table table-sm modern-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ width: '30%', fontSize: '11px' }}>Produit</th>
                                                                        <th className="text-center" style={{ width: '8%', fontSize: '11px' }}>Commandée</th>
                                                                            <th className="text-center" style={{ width: '8%', fontSize: '11px' }}>Expédié</th>
                                                                            <th className="text-center" style={{ width: '10%', fontSize: '11px' }}>à Préparer</th>
                                                                        <th className="text-center" style={{ width: '8%', fontSize: '11px' }}>Stock</th>
                                                                        <th className="text-center" style={{ width: '15%', fontSize: '11px' }}>Entrepôt</th>
                                                                        <th className="text-center" style={{ width: '11%', fontSize: '11px' }}>Cmd fourn.</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {expeditionData.lines.map((line: ExpeditionLine) => {
                                                                        const selectedWarehouse = selectedWarehouses[line.id] ||
                                                                            (line.stock.length > 0 ? line.stock[0].entrepot_id : '');
                                                                        const selectedStock = line.stock.find(s => s.entrepot_id === selectedWarehouse);
                                                                        const orderedQty = parseInt(line.qty);
                                                                        const alreadyShipped = line.alreadyshipped || 0;
                                                                        const maxPreparable = Math.max(0, orderedQty - alreadyShipped);
                                                                        const isFullyShipped = alreadyShipped >= orderedQty;
                                                                        const preparedNow = preparedQuantities[line.id] || 0;

                                                                        return (
                                                                            <tr key={line.id} style={{ fontSize: '12px' }}>
                                                                                <td style={{ maxWidth: '250px' }}>
                                                                                    <div className="text-truncate-custom" title={line.desc}>
                                                                                        <span className="fw-semibold text-primary" >{line.ref}</span> - {line.desc}
                                                                                    </div>
                                                                                    {line.comment && (
                                                                                        <div
                                                                                            className="text-muted"
                                                                                            title={line.comment}
                                                                                            style={{ fontSize: '11px', marginTop: '2px' }}
                                                                                        >
                                                                                            <i className="ri-chat-1-line me-1" style={{ fontSize: '10px' }}></i>
                                                                                            {line.comment.length > 30 ? line.comment.substring(0, 30) + '...' : line.comment}
                                                                                        </div>
                                                                                    )}
                                                                                </td>
                                                                                <td className="text-center" style={{ fontSize: '11px' }}>
                                                                                    {line.qty}
                                                                                </td>
                                                                                <td className="text-center" style={{ fontSize: '11px' }}>
                                                                                    {alreadyShipped}
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <div>
                                                                                        <Input
                                                                                            type="number"
                                                                                            min="0"
                                                                                            max={maxPreparable}
                                                                                            value={preparedNow}
                                                                                            onChange={(e) => handleQuantityChange(line.id, e.target.value)}
                                                                                            onKeyDown={(e) => {
                                                                                                // Empêcher la saisie de caractères non numériques
                                                                                                if (!/[0-9]/.test(e.key) &&
                                                                                                    e.key !== 'Backspace' &&
                                                                                                    e.key !== 'Delete' &&
                                                                                                    e.key !== 'Tab' &&
                                                                                                    e.key !== 'ArrowLeft' &&
                                                                                                    e.key !== 'ArrowRight' &&
                                                                                                    e.key !== 'Home' &&
                                                                                                    e.key !== 'End') {
                                                                                                    e.preventDefault();
                                                                                                }
                                                                                            }}
                                                                                            onBlur={(e) => {
                                                                                                // Si vide, remettre à 0
                                                                                                if (e.target.value === '') {
                                                                                                    handleQuantityChange(line.id, '0');
                                                                                                }
                                                                                            }}
                                                                                            className="modern-input text-center"
                                                                                            style={{
                                                                                                width: '60px',
                                                                                                margin: '0 auto',
                                                                                                fontSize: '11px',
                                                                                                borderColor: (preparedNow < 0 || preparedNow > maxPreparable) ? '#ef4444' : '',
                                                                                                boxShadow: (preparedNow < 0 || preparedNow > maxPreparable) ? '0 0 0 2px rgba(239, 68, 68, 0.1)' : ''
                                                                                            }}
                                                                                            disabled={isFullyShipped}
                                                                                        />
                                                                                        {maxPreparable > 0 && (
                                                                                            <small className="quantity-info">
                                                                                                Max: {maxPreparable}
                                                                                            </small>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <Badge
                                                                                        color={selectedStock && parseInt(selectedStock.stock) > 0 ? "success" : "danger"}
                                                                                        style={{ fontSize: '11px', padding: '3px 6px' }}
                                                                                    >
                                                                                        {selectedStock ? selectedStock.stock : '0'}
                                                                                    </Badge>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    {line.stock.length > 0 ? (
                                                                                        <Dropdown
                                                                                            isOpen={dropdownOpen[line.id] || false}
                                                                                            toggle={() => toggleDropdown(line.id)}
                                                                                            direction="down"
                                                                                        >
                                                                                            <DropdownToggle
                                                                                                caret
                                                                                                className="dropdown-toggle-custom dropdown-toggle-sm"
                                                                                                style={{ minWidth: '100px' }}
                                                                                            >
                                                                                                <span className="text-truncate-custom" style={{ display: 'inline-block', maxWidth: '80px' }}>
                                                                                                    {selectedStock ? selectedStock.entrepot_name : 'Sélectionner'}
                                                                                                </span>
                                                                                            </DropdownToggle>
                                                                                            <DropdownMenu className="dropdown-menu-custom">
                                                                                                {line.stock.map((stockItem: Stock) => (
                                                                                                    <DropdownItem
                                                                                                        key={stockItem.entrepot_id}
                                                                                                        onClick={() => handleWarehouseChange(line.id, stockItem.entrepot_id)}
                                                                                                        active={selectedWarehouse === stockItem.entrepot_id}
                                                                                                        className="dropdown-item-custom"
                                                                                                    >
                                                                                                        <div className="d-flex justify-content-between align-items-center w-100">
                                                                                                            <span className="text-truncate-custom" style={{ maxWidth: '100px' }}>
                                                                                                                {stockItem.entrepot_name}
                                                                                                            </span>
                                                                                                            <Badge
                                                                                                                color={parseInt(stockItem.stock) > 0 ? "success" : "danger"}
                                                                                                                className="ms-2"
                                                                                                                style={{ fontSize: '9px', minWidth: '30px', textAlign: 'center' }}
                                                                                                            >
                                                                                                                {stockItem.stock}
                                                                                                            </Badge>
                                                                                                        </div>
                                                                                                    </DropdownItem>
                                                                                                ))}
                                                                                            </DropdownMenu>
                                                                                        </Dropdown>
                                                                                    ) : (
                                                                                        <Badge color="warning" style={{ fontSize: '10px' }}>Pas de stock</Badge>
                                                                                    )}
                                                                                </td>

                                                                                <td className="text-center">
                                                                                    {!line.commandefourndet ||
                                                                                        line.commandefourndet === "0" ||
                                                                                        line.commandefourndet === "null" ||
                                                                                        line.commandefourndet === "" ? (
                                                                                        <Button
                                                                                            color="primary"
                                                                                            size="sm"
                                                                                            className="action-btn commander-btn"
                                                                                            onClick={() => openCommanderModal(line)}
                                                                                            disabled
                                                                                        >
                                                                                            <i className="ri-shopping-cart-line me-1"></i>
                                                                                            Commander
                                                                                        </Button>

                                                                                    ) : (
                                                                                        <Button
                                                                                            color="success"
                                                                                            size="sm"
                                                                                            className="action-btn voir-btn"
                                                                                            onClick={() => openVoirModal(line)}
                                                                                        >
                                                                                            <i className="ri-eye-line me-1"></i>
                                                                                            Voir
                                                                                        </Button>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        <div className="mt-3" style={{ fontSize: '12px' }}>
                                                            <Row className="align-items-center">
                                                                <Col md={6}>
                                                                    <div className="d-flex gap-4">
                                                                        <div>
                                                                            <small className="text-muted">Total commandé:</small>
                                                                            <div className="fw-bold">{totalOrdered}</div>
                                                                        </div>
                                                                        <div>
                                                                            <small className="text-muted">Déjà expédié:</small>
                                                                            <div className="fw-bold">{totalAlreadyShipped}</div>
                                                                        </div>
                                                                        <div>
                                                                            <small className="text-muted">À préparer:</small>
                                                                            <div className="fw-bold">{totalPrepared}</div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6} className="text-end">
                                                                    <div className="d-flex gap-2 justify-content-end">
                                                                        {/* Bouton de clôture - affiché seulement si conditions remplies */}
                                                                        {canCloseOrder() && (
                                                                           <Button
                                                                                className="close-order-btn"
                                                                               onClick={() => {
                                                                      // Réinitialiser toutes les quantités à 0 pour la clôture
                                                                       const zeroQuantities: Record<string, number> = {};
                                                                       expeditionData?.lines?.forEach(line => {
                                                                     zeroQuantities[line.id] = 0;
                                                                     });
                                                                    setPreparedQuantities(zeroQuantities);
        
                                                               // Ouvrir la modal de confirmation
                                                                 setConfirmClosureModalOpen(true);
                                                                   }}
                                                             disabled={saving}
                                                              style={{
                                                             padding: '6px 16px',
                                                             fontSize: '12px',
                                                            fontWeight: 600
                                                             }}
                                                             >
                                                             <i className="ri-close-circle-line me-2"></i>
                                                            Clôturer Commande
                                                              </Button>
                                                                        )}
                                                                        
                                                                        <Button
                                                                            className="validate-btn"
                                                                            id="btnvalider"
                                                                            onClick={handleValidateExpedition}
                                                                            disabled={saving || isExpeditionComplete || !areAllQuantitiesValid() || !isAnyQuantityPrepared()}
                                                                            style={{
                                                                                padding: '6px 16px',
                                                                                fontSize: '12px',
                                                                                backgroundColor: (!areAllQuantitiesValid() || !isAnyQuantityPrepared()) ? '#B0C4DE' : (!isAllLinesValidated() ? '#00A896' : '#F4A261'),
                                                                                borderColor: (!areAllQuantitiesValid() || !isAnyQuantityPrepared()) ? '#B0C4DE' : (!isAllLinesValidated() ? '#00A896' : '#F4A261'),
                                                                                cursor: (!areAllQuantitiesValid() || !isAnyQuantityPrepared()) ? 'not-allowed' : 'pointer'
                                                                            }}
                                                                        >
                                                                            {saving ? (
                                                                                <>
                                                                                    <Spinner size="sm" className="me-2" />
                                                                                    En cours...
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <i className="ri-check-double-line me-2"></i>
                                                                                    {!isAnyQuantityPrepared()
                                                                                        ? 'Aucune quantité'
                                                                                        : !areAllQuantitiesValid()
                                                                                            ? 'Quantités invalides'
                                                                                            : !isAllLinesValidated()
                                                                                                ? 'Valider l\'Expédition'
                                                                                                : 'Confirmer l\'Expédition'}
                                                                                </>
                                                                            )}
                                                                        </Button>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-4">
                                                        <i className="ri-error-warning-line display-5 text-muted"></i>
                                                        <p className="text-muted mt-2" style={{ fontSize: '12px' }}>Aucune commande sélectionnée.</p>
                                                    </div>
                                                )}
                                            </CardBody>
                                        </Card>
                                    ) : (
                                        <Card className="no-commande-card">
                                            <CardBody>
                                                <h5 style={{ fontSize: '16px', color: '#6B7280', marginBottom: '8px' }}>
                                                    Aucune commande sélectionnée
                                                </h5>
                                                <p style={{ fontSize: '12px', color: '#6B7280', marginBottom: 0 }}>
                                                    Veuillez sélectionner une commande pour afficher les détails.
                                                </p>
                                            </CardBody>
                                        </Card>
                                    )}
                                </Col>
                                <Col xl="3" lg="4" style={{ paddingLeft: '4px' }}>
                                    <div className="position-sticky" style={{ top: "80px" }}>
                                        <Card className="commande-list-container">
                                            <CardHeader style={{
                                                backgroundColor: '#0077B6',
                                                borderRadius: '8px 8px 0 0',
                                                padding: '6px 10px',
                                                textAlign: 'center'
                                            }}>
                                                <h6 style={{
                                                    fontSize: '12px',
                                                    color: 'white',
                                                    fontWeight: 400,
                                                    margin: 0
                                                }}>
                                                    Liste des Commandes en attentes
                                                </h6>
                                            </CardHeader>
                                            <CardBody style={{ padding: '12px' }}>
                                                {/* Tab Navigation */}
                                                <div className="tab-header mb-3">
                                                    <Nav pills className="w-100 d-flex">
                                                        <NavItem className="flex-grow-1 text-center">
                                                            <NavLink
                                                                className={`tab-nav-link ${activeTab === 'comptoir' ? 'active' : ''}`}
                                                                onClick={() => setActiveTab('comptoir')}
                                                            >
                                                                Comptoir ({comptoirOrders.length})
                                                            </NavLink>
                                                        </NavItem>
                                                        <NavItem className="flex-grow-1 text-center">
                                                            <NavLink
                                                                className={`tab-nav-link ${activeTab === 'franchises' ? 'active' : ''}`}
                                                                onClick={() => setActiveTab('franchises')}
                                                            >
                                                                Franchises ({franchisesOrders.length})
                                                            </NavLink>
                                                        </NavItem>
                                                    </Nav>
                                                </div>

                                                {/* Orders List */}
                                                <div className="orders-scroll-container">
                                                    {allOrders.length > 0 ? (
                                                        <ul className="list-group" style={{ listStyle: 'none', padding: 0 }}>
                                                            {allOrders.map((commande) => {
                                                                const hasExpeditions = commande.has_expeditions === true;
                                                                const hasDeliveryDate = commande.date_livraison && commande.date_livraison !== '0000-00-00';
                                                                const shouldHighlight = hasExpeditions && hasDeliveryDate;
                                                                const hasDispatchedItems = (commande.commandesfournisseurs?.qty_dispatched ?? 0) > 0;
                                                                return (
                                                                    <li
                                                                        key={commande.id}
                                                                        className={`commande-list-item ${order?.id === commande.id ? 'active' : ''}`}
                                                                        onClick={() => handleCommandeSelect(commande)}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            padding: '8px 10px',
                                                                            marginBottom: '6px',
                                                                            border: '1px solid #E5E7EB',
                                                                            borderRadius: '6px',
                                                                            position: 'relative',
                                                                            overflow: 'visible',
                                                                            fontSize: '12px'
                                                                        }}
                                                                    >
                                                                        {hasDispatchedItems && (

                                                                            <Badge style={{
                                                                                position: 'absolute',
                                                                                bottom: '6px',
                                                                                right: '6px',
                                                                                zIndex: 1,
                                                                            }} color="warning"> Pièces Reçues </Badge>
                                                                        )}

                                                                        <div className="d-flex justify-content-between align-items-center">
                                                                            <span style={{
                                                                                fontSize: '12px',
                                                                                fontWeight: 600,
                                                                                color: '#1F2937'
                                                                            }}>
                                                                                {commande.ref}
                                                                            </span>
                                                                            <Badge className="modern-badge badge bg-info" style={{ fontSize: '9px', padding: '1px 4px' }}>
                                                                                {commande.author_fullname?.substring(0, 10)}
                                                                            </Badge>
                                                                        </div>
                                                                        <span style={{
                                                                            fontSize: '11px',
                                                                            fontWeight: 600,
                                                                            color: '#028090'
                                                                        }}>
                                                                            {commande.client?.length > 20 ? commande.client.substring(0, 20) + '...' : commande.client}
                                                                        </span>
                                                                        <small style={{
                                                                            fontSize: '10px',
                                                                            color: '#6B7280',
                                                                            display: 'block',
                                                                            marginTop: '2px'
                                                                        }}>
                                                                            {new Date(commande.date).toLocaleDateString()}
                                                                        </small>
                                                                    </li>
                                                                );
                                                            })}
                                                        </ul>
                                                    ) : (
                                                        <div className="text-center py-4">
                                                            <i className="ri-error-warning-line text-muted mb-2" style={{ fontSize: '24px' }}></i>
                                                            <p style={{
                                                                fontSize: '11px',
                                                                color: '#6B7280',
                                                                textAlign: 'center',
                                                                padding: '12px 0',
                                                                margin: 0
                                                            }}>
                                                                {activeTab === 'comptoir'
                                                                    ? 'Aucune commande de comptoir à préparer.'
                                                                    : 'Aucune commande de franchise à préparer.'}
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default WaitingOrders;